import { useParams, useNavigate } from 'react-router-dom';
import { useCourse, useMaterials, useGrades, useCertificates, useAttendance, useSessions } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  ArrowLeft,
  BookOpen, 
  Award,
  Calendar,
  Clock,
  Loader2,
  Download,
  ExternalLink,
  FileText,
  File,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const levelColors = {
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const materialTypeIcons = {
  pdf: File,
  video: Play,
  exercise: FileText,
  link: ExternalLink,
  other: FileText,
};

const statusColors = {
  present: 'text-success',
  late: 'text-warning',
  absent: 'text-destructive',
};

const statusLabels = {
  present: 'Présent',
  late: 'Retard',
  absent: 'Absent',
};

const statusIcons = {
  present: CheckCircle,
  late: AlertCircle,
  absent: XCircle,
};

const StudentCourseDetail = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { data: course, isLoading: courseLoading } = useCourse(id);
  const { data: materials } = useMaterials(id);
  const { data: grades } = useGrades(id);
  const { data: certificates } = useCertificates(null, id);
  const { data: sessions } = useSessions(id);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate average grade
  const averageGrade = grades?.length > 0
    ? Math.round(grades.reduce((sum, g) => sum + (g.score / g.max_score * 100), 0) / grades.length)
    : null;

  if (courseLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Cours non trouvé</h2>
        <Button variant="outline" onClick={() => navigate('/student/courses')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-course-detail">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/student/courses')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour à mes cours
      </Button>

      {/* Course Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">{course.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={levelColors[course.level]}>{course.level}</Badge>
            {course.teacher_name && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="bg-secondary text-white text-xs">
                    {course.teacher_name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm">{course.teacher_name}</span>
              </div>
            )}
          </div>
          {course.description && (
            <p className="text-muted-foreground mt-2">{course.description}</p>
          )}
        </div>

        {averageGrade !== null && (
          <Card className="border-0 shadow-sm min-w-[150px]">
            <CardContent className="text-center py-4">
              <p className="text-sm text-muted-foreground">Ma moyenne</p>
              <p className="text-3xl font-bold font-heading text-primary">{averageGrade}%</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="materials" className="space-y-6">
        <TabsList className="w-full justify-start bg-muted/50 p-1">
          <TabsTrigger value="materials">{t('course_detail_materials', lang)}</TabsTrigger>
          <TabsTrigger value="grades">Mes notes</TabsTrigger>
          <TabsTrigger value="attendance">Mes présences</TabsTrigger>
          <TabsTrigger value="certificates">{t('course_detail_certificates', lang)}</TabsTrigger>
        </TabsList>

        {/* Tab: Materials */}
        <TabsContent value="materials" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Matériaux pédagogiques ({materials?.length || 0})
              </CardTitle>
              <CardDescription>
                Téléchargez les supports de cours et exercices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {materials?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun matériau disponible pour ce cours
                </p>
              ) : (
                <div className="space-y-3">
                  {materials?.map((material) => {
                    const Icon = materialTypeIcons[material.type] || FileText;
                    return (
                      <div key={material.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{material.title}</p>
                            {material.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {material.description}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Ajouté le {formatDate(material.created_at)}
                            </p>
                          </div>
                        </div>
                        <Button variant="default" asChild>
                          <a href={material.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Ouvrir
                          </a>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Grades */}
        <TabsContent value="grades" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-warning" />
                Mes notes ({grades?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {grades?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune note enregistrée
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Évaluation</TableHead>
                      <TableHead className="text-center">Note</TableHead>
                      <TableHead className="text-center">Pourcentage</TableHead>
                      <TableHead>{t('form_comment', lang)}</TableHead>
                      <TableHead>{t('form_date', lang)}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grades?.map((grade) => {
                      const percentage = Math.round(grade.score / grade.max_score * 100);
                      return (
                        <TableRow key={grade.id}>
                          <TableCell className="font-medium">{grade.evaluation_title}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="font-mono">
                              {grade.score} / {grade.max_score}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={percentage >= 60 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}>
                              {percentage}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {grade.comment || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(grade.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Attendance */}
        <TabsContent value="attendance" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-secondary" />
                Historique de présence
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sessions?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune session passée
                </p>
              ) : (
                <div className="space-y-3">
                  {sessions?.filter(s => new Date(s.date) < new Date()).map((session) => {
                    // We would need to fetch individual attendance, for now show session info
                    const summary = session.attendance_summary;
                    return (
                      <div key={session.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{session.title}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(session.date)} • {session.duration_minutes} min
                            </p>
                          </div>
                        </div>
                        {summary && (
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1 text-success">
                              <CheckCircle className="w-4 h-4" />
                              {summary.present}
                            </span>
                            <span className="flex items-center gap-1 text-warning">
                              <AlertCircle className="w-4 h-4" />
                              {summary.late}
                            </span>
                            <span className="flex items-center gap-1 text-destructive">
                              <XCircle className="w-4 h-4" />
                              {summary.absent}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Certificates */}
        <TabsContent value="certificates" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Certificats obtenus
              </CardTitle>
            </CardHeader>
            <CardContent>
              {certificates?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun certificat délivré pour ce cours
                </p>
              ) : (
                <div className="space-y-3">
                  {certificates?.map((cert) => (
                    <div key={cert.id} className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-primary/5 to-secondary/5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Award className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">Certificat de niveau {cert.level}</p>
                          <p className="text-sm text-muted-foreground">
                            Délivré le {formatDate(cert.issued_at)}
                          </p>
                        </div>
                      </div>
                      <Button asChild>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${cert.pdf_url}`} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentCourseDetail;
